// components/shop/VariantsTab.tsx
// Variants management tab — used inside ProductsPage.tsx edit modal
// Handles: add / edit / delete / stock update / option types

import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import BusinessApiService from "../../services/BusinessApiService";

const API_BASE = process.env.REACT_APP_API_URL || "";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface Variant {
  _id?: string;
  name: string;
  sku: string;
  stock: number;
  image?: string;
  isActive: boolean;
  weight?: number;
  priceModifier: number;
  priceModifierType: 'fixed' | 'percent';
  options: Record<string, string>;
  calculatedPrice?: number;
  calculatedCurrency?: string;
}

interface ProductForVariants {
  productId?: string;
  _id?: string;
  pricing?: { retail?: number; currency?: string };
  // marketPricing removed — price is flat on product.pricing
}

// ── Constants ─────────────────────────────────────────────────────────────────
const EMPTY_VARIANT: Variant = {
  name: '', sku: '', stock: 0, image: '', isActive: true,
  weight: 0, priceModifier: 0, priceModifierType: 'fixed', options: {},
};

// ── Helper: market flag ───────────────────────────────────────────────────────
const MARKET_FLAGS: Record<string, string> = {
  VN:'🇻🇳', US:'🇺🇸', SG:'🇸🇬', JP:'🇯🇵', AU:'🇦🇺', GB:'🇬🇧',
  CA:'🇨🇦', DE:'🇩🇪', FR:'🇫🇷', KR:'🇰🇷', TH:'🇹🇭', MY:'🇲🇾',
  ID:'🇮🇩', PH:'🇵🇭', AE:'🇦🇪', SA:'🇸🇦', GLOBAL:'🌍', DEFAULT:'🏷️',
};
const flag = (code: string) => MARKET_FLAGS[code] || '🌐';

// ── AddValueInline ─────────────────────────────────────────────────────────────
const AddValueInline: React.FC<{ onAdd: (val: string) => void; placeholder: string }> = ({ onAdd, placeholder }) => {
  const [val, setVal] = useState('');
  return (
    <div className="flex gap-1">
      <input type="text" value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { onAdd(val); setVal(''); } }}
        placeholder={placeholder}
        className="border border-gray-200 rounded px-2 py-1 text-xs flex-1 min-w-0" />
      <button onClick={() => { onAdd(val); setVal(''); }}
        className="bg-indigo-600 text-white text-xs px-2 py-1 rounded hover:bg-indigo-700">+</button>
    </div>
  );
};

// ── VariantEditForm ────────────────────────────────────────────────────────────
const VariantEditForm: React.FC<{
  variant: Variant;
  optionKeys: string[];
  baseCurrency: string;
  basePrice: number;
  onSave: (data: Partial<Variant>) => void;
  onCancel: () => void;
  saving: boolean;
}> = ({ variant, optionKeys, baseCurrency, basePrice, onSave, onCancel, saving }) => {
  const [data, setData] = useState<Variant>({ ...variant });

  return (
    <div className="p-3 bg-indigo-50 border-t border-indigo-100">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs text-gray-600 block mb-1">Variant Name</label>
          <input type="text" value={data.name} onChange={e => setData(d => ({ ...d, name: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">SKU</label>
          <input type="text" value={data.sku} onChange={e => setData(d => ({ ...d, sku: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Stock</label>
          <input type="number" min="0" value={data.stock} onChange={e => setData(d => ({ ...d, stock: parseInt(e.target.value) || 0 }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-gray-600 block mb-1">Weight (optional)</label>
          <input type="number" step="0.01" min="0" value={data.weight || ''}
            onChange={e => setData(d => ({ ...d, weight: parseFloat(e.target.value) || 0 }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            placeholder="e.g. 250 (grams)" />
        </div>
        {optionKeys.map(key => (
          <div key={key}>
            <label className="text-xs text-gray-600 block mb-1 capitalize">{key}</label>
            <input type="text" value={(data.options || {})[key] || ''}
              onChange={e => setData(d => ({ ...d, options: { ...(d.options || {}), [key]: e.target.value } }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-3">
        <button onClick={() => onSave(data)} disabled={saving}
          className="bg-green-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={onCancel} className="border border-gray-300 px-5 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
};

// ── VariantsTab ────────────────────────────────────────────────────────────────
const VariantsTab: React.FC<{
  product: ProductForVariants;
  onRefresh: () => void;
}> = ({ product, onRefresh }) => {
  const [variants, setVariants]               = useState<Variant[]>([]);
  const [optionKeys, setOptionKeys]           = useState<string[]>(['size', 'color']);
  const [optionValues, setOptionValues]       = useState<Record<string, string[]>>({});
  const [showAddForm, setShowAddForm]         = useState(false);
  const [newVariant, setNewVariant]           = useState<Variant>({ ...EMPTY_VARIANT });
  const [saving, setSaving]                   = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(true);
  const [editVariantId, setEditVariantId]     = useState<string | null>(null);
  const [newOptionKey, setNewOptionKey]       = useState('');
  const [stockEdit, setStockEdit]             = useState<{ id: string; value: string } | null>(null);

  const token = BusinessApiService.getBusinessToken();
  const pid   = product.productId || product._id;

  // ── Load variants ──────────────────────────────────────────────────────────
  const loadVariants = async () => {
    setLoadingVariants(true);
    try {
      const res = await fetch(`${API_BASE}/products/${pid}/variants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setVariants(data.data.variants || []);
        const opts = data.data.variantOptions || {};
        const optsObj = typeof opts === 'object' && !(opts instanceof Map)
          ? opts : Object.fromEntries(opts);
        setOptionValues(optsObj);
        setOptionKeys(Object.keys(optsObj).length > 0 ? Object.keys(optsObj) : ['size', 'color']);
      }
    } catch (err: any) {
      console.error('Failed to load variants:', err.message);
    } finally {
      setLoadingVariants(false);
    }
  };

  useEffect(() => { if (pid) loadVariants(); }, [pid]);

  // ── API helper ─────────────────────────────────────────────────────────────
  const apiCall = async (method: string, path: string, body?: any) => {
    const res = await fetch(`${API_BASE}/products/${pid}${path}`, {
      method,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    return { ok: res.ok, data: await res.json() };
  };

  // ── CRUD handlers ──────────────────────────────────────────────────────────
  const handleAddVariant = async () => {
    if (!newVariant.name.trim()) { alert('Variant name is required'); return; }
    setSaving(true);
    const { ok, data } = await apiCall('POST', '/variants', {
      name: newVariant.name.trim(), sku: newVariant.sku,
      stock: newVariant.stock, priceModifier: newVariant.priceModifier,
      priceModifierType: newVariant.priceModifierType,
      options: newVariant.options, image: newVariant.image,
      weight: newVariant.weight || null, isActive: true,
    });
    setSaving(false);
    if (ok) { await loadVariants(); setNewVariant({ ...EMPTY_VARIANT }); setShowAddForm(false); onRefresh(); }
    else alert(data.message || data.error || 'Failed to add variant');
  };

  const handleEditSave = async (variantId: string, updates: Partial<Variant>) => {
    setSaving(true);
    const { ok, data } = await apiCall('PUT', `/variants/${variantId}`, updates);
    setSaving(false);
    if (ok) { await loadVariants(); setEditVariantId(null); onRefresh(); }
    else alert(data.message || data.error || 'Failed to update variant');
  };

  const handleDeleteVariant = async (v: Variant) => {
    if (!window.confirm(`Remove variant "${v.name}"?`)) return;
    const id = v._id || v.sku;
    setSaving(true);
    const { ok, data } = await apiCall('DELETE', `/variants/${id}`);
    setSaving(false);
    if (ok) { await loadVariants(); onRefresh(); }
    else alert(data.message || data.error || 'Failed to remove variant');
  };

  const handleToggleActive = async (v: Variant) => {
    await handleEditSave((v._id || v.sku)!, { isActive: !v.isActive });
  };

  const handleStockUpdate = async (v: Variant, newStock: string) => {
    const id = v._id || v.sku;
    setSaving(true);
    const { ok, data } = await apiCall('PATCH', `/variants/${id}/stock`, { stock: parseInt(newStock) || 0, type: 'set' });
    setSaving(false);
    if (ok) { await loadVariants(); setStockEdit(null); }
    else alert(data.message || data.error || 'Failed to update stock');
  };

  // ── Option management ──────────────────────────────────────────────────────
  const saveOptions = async (updatedOptions: Record<string, string[]>) => {
    await apiCall('PUT', '', { variantOptions: updatedOptions, hasVariants: variants.length > 0 });
    setOptionValues(updatedOptions);
  };

  const handleAddOptionKey = () => {
    if (!newOptionKey.trim()) return;
    const key = newOptionKey.trim().toLowerCase();
    if (optionKeys.includes(key)) return;
    setOptionKeys(k => [...k, key]);
    setNewOptionKey('');
    saveOptions({ ...optionValues, [key]: [] });
  };

  const handleAddOptionValue = (key: string, value: string) => {
    if (!value.trim() || (optionValues[key] || []).includes(value.trim())) return;
    saveOptions({ ...optionValues, [key]: [...(optionValues[key] || []), value.trim()] });
  };

  const handleRemoveOptionValue = (key: string, val: string) => {
    saveOptions({ ...optionValues, [key]: (optionValues[key] || []).filter(v => v !== val) });
  };

  // ── Base price for preview ─────────────────────────────────────────────────
  const baseMarket = { price: product.pricing?.retail || 0, currency: product.pricing?.currency || 'USD' };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* How it works */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800">
        <b>How variant pricing works:</b> Each variant has a <b>price modifier</b> added to the base
        market price. Set +80% for a 500g bag and it automatically applies to VND, USD, JPY — every market.
      </div>

      {/* Option types */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
        <h5 className="font-semibold text-gray-900 mb-1">Option Types</h5>
        <p className="text-xs text-gray-500 mb-3">Define variant dimensions: size, color, weight, material, etc.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {optionKeys.map(key => (
            <div key={key} className="bg-white border border-gray-200 rounded-lg px-3 py-2 min-w-[120px]">
              <p className="text-xs font-semibold text-gray-700 capitalize mb-1.5">{key}</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {(optionValues[key] || []).map(val => (
                  <span key={val} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
                    {val}
                    <button onClick={() => handleRemoveOptionValue(key, val)} className="hover:text-red-500 leading-none ml-0.5">×</button>
                  </span>
                ))}
              </div>
              <AddValueInline onAdd={val => handleAddOptionValue(key, val)} placeholder={`Add ${key}...`} />
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" placeholder="New option type (e.g. weight, material)" value={newOptionKey}
            onChange={e => setNewOptionKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddOptionKey()}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1" />
          <button onClick={handleAddOptionKey}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700">+ Add</button>
        </div>
      </div>

      {/* Base price */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
        <p className="text-amber-800 font-medium">⚠️ Prices are controlled per country in Shop Listings</p>
        <p className="text-amber-600 text-xs mt-0.5">Edit a Shop Listing → 🎨 Variants tab → set price per variant in local currency (VND, USD, THB...)</p>
      </div>

      {/* Variants list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-semibold text-gray-900">Variants ({variants.length})</h5>
          {!showAddForm && (
            <button onClick={() => setShowAddForm(true)}
              className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2">
              <Plus size={14} /> Add Variant
            </button>
          )}
        </div>

        {loadingVariants ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-400 mx-auto mb-2" />
            Loading variants...
          </div>
        ) : variants.length === 0 && !showAddForm ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
            <div className="text-3xl mb-2">🎨</div>
            <p className="text-gray-500 text-sm">No variants yet</p>
            <p className="text-gray-400 text-xs mt-1">Add size, color, weight options for this product</p>
          </div>
        ) : (
          <div className="space-y-2">
            {variants.map(v => {
              const vid = v._id || v.sku;
              const isEditing = editVariantId === vid;
              return (
                <div key={vid} className={`border rounded-xl overflow-hidden transition-opacity ${!v.isActive ? 'opacity-60' : ''} ${isEditing ? 'border-indigo-400 shadow-md' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3 p-3 bg-white">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">{v.name}</span>
                        {v.sku && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-mono">{v.sku}</span>}
                        {!v.isActive && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Disabled</span>}
                      </div>
                      {!isEditing && (
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                          {/* Inline stock edit */}
                          {stockEdit?.id === vid ? (
                            <span className="flex items-center gap-1">
                              Stock:
                              <input type="number" min="0" value={stockEdit.value} autoFocus
                                onChange={e => setStockEdit(s => s ? { ...s, value: e.target.value } : null)}
                                className="w-16 border border-indigo-300 rounded px-1 py-0.5 text-xs" />
                              <button onClick={() => handleStockUpdate(v, stockEdit.value)} className="text-green-600 font-bold px-1">✓</button>
                              <button onClick={() => setStockEdit(null)} className="text-gray-400 px-1">✕</button>
                            </span>
                          ) : (
                            <span onClick={() => setStockEdit({ id: vid!, value: String(v.stock) })}
                              className="cursor-pointer hover:text-indigo-600 select-none">
                              Stock: <b>{v.stock}</b> <span className="opacity-50">✎</span>
                            </span>
                          )}
                          <span className="text-indigo-500 italic">
                            prices set per country in Shop Listings
                          </span>
                          {Object.entries(v.options || {}).map(([k, val]) => (
                            <span key={k} className="bg-gray-100 px-2 py-0.5 rounded-full capitalize">{k}: {val}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!isEditing ? (
                        <>
                          <button onClick={() => setEditVariantId(vid!)}
                            className="text-xs bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-lg hover:bg-yellow-100 border border-yellow-200">Edit</button>
                          <button onClick={() => handleToggleActive(v)}
                            className={`text-xs px-3 py-1.5 rounded-lg border ${v.isActive ? 'bg-gray-50 text-gray-600 border-gray-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                            {v.isActive ? 'Disable' : 'Enable'}
                          </button>
                          <button onClick={() => handleDeleteVariant(v)} disabled={saving}
                            className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 border border-red-200 disabled:opacity-50">Remove</button>
                        </>
                      ) : (
                        <button onClick={() => setEditVariantId(null)}
                          className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50">Cancel</button>
                      )}
                    </div>
                  </div>
                  {isEditing && (
                    <VariantEditForm
                      variant={v}
                      optionKeys={optionKeys}
                      baseCurrency={(baseMarket as any).currency || 'USD'}
                      basePrice={(baseMarket as any).price || 0}
                      onSave={data => handleEditSave(vid!, data)}
                      onCancel={() => setEditVariantId(null)}
                      saving={saving}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add variant form */}
        {showAddForm && (
          <div className="mt-3 border-2 border-indigo-300 border-dashed rounded-xl p-4 bg-indigo-50">
            <h6 className="font-semibold text-indigo-900 mb-3 text-sm">Add New Variant</h6>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs text-gray-600 block mb-1">Variant Name * (e.g. "250g Bag", "Red / XL")</label>
                <input type="text" placeholder="Variant name" value={newVariant.name}
                  onChange={e => setNewVariant(d => ({ ...d, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">SKU</label>
                <input type="text" placeholder="PROD-001-V1" value={newVariant.sku}
                  onChange={e => setNewVariant(d => ({ ...d, sku: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Stock</label>
                <input type="number" min="0" value={newVariant.stock}
                  onChange={e => setNewVariant(d => ({ ...d, stock: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
              {/* Pricing note */}
              <div className="md:col-span-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                💡 <b>Prices per country are set in Shop Listings.</b> Go to <b>🛒 Shop Listings</b> → Edit listing → <b>🎨 Variants</b> tab to set VND, USD, THB prices per variant.
              </div>
              {/* Option selectors */}
              {optionKeys.map(key => (
                <div key={key}>
                  <label className="text-xs text-gray-600 block mb-1 capitalize">{key}</label>
                  <select value={newVariant.options[key] || ''}
                    onChange={e => setNewVariant(d => ({ ...d, options: { ...d.options, [key]: e.target.value } }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Select {key}...</option>
                    {(optionValues[key] || []).map(val => <option key={val} value={val}>{val}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleAddVariant} disabled={saving}
                className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Adding...' : 'Add Variant'}
              </button>
              <button onClick={() => { setShowAddForm(false); setNewVariant({ ...EMPTY_VARIANT }); }}
                className="border border-gray-300 px-5 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VariantsTab;